(() => {
  'use strict';

  const ELEMENT_NAME = 'lavinde-slider-tabs-v2';

  if (customElements.get(ELEMENT_NAME)) return;

  class LavindeSliderTabsV2 extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.boundResize = () => {
        this.ensureMobileControls();
        this.renderDots();
        this.requestUpdate();
      };

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      window.addEventListener('resize', this.boundResize, { passive: true });

      this.ensureMobileControls();
      this.attachTrackListeners();
      this.scrollTabIntoView(this.activeTabIndex, false);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.ensureMobileControls();
          this.renderDots();
          this.requestUpdate();
        });
        this.resizeObserver.observe(this);
      }

      setTimeout(() => {
        this.ensureMobileControls();
        this.renderDots();
        this.requestUpdate();
      }, 120);

      this.requestUpdate();
    }

    disconnectedCallback() {
      if (this.boundResize) window.removeEventListener('resize', this.boundResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      cancelAnimationFrame(this.updateFrame);
    }

    onClick(event) {
      const tab = event.target.closest('[data-lavinde-slider-tab]');
      if (tab && this.contains(tab)) {
        event.preventDefault();
        this.selectTab(Number(tab.dataset.lavindeSliderTab || 0), true);
        return;
      }

      const dot = event.target.closest('[data-lavinde-slider-dot]');
      if (dot && this.contains(dot)) {
        event.preventDefault();
        this.scrollToPageIndex(Number(dot.dataset.lavindeSliderDot || 0));
        return;
      }

      const prev = event.target.closest('[data-lavinde-slider-prev]');
      if (prev && this.contains(prev)) {
        event.preventDefault();
        this.scrollActiveTrack(-1, prev.closest('[data-lavinde-slider-mobile-controls]'));
        return;
      }

      const next = event.target.closest('[data-lavinde-slider-next]');
      if (next && this.contains(next)) {
        event.preventDefault();
        this.scrollActiveTrack(1, next.closest('[data-lavinde-slider-mobile-controls]'));
      }
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-lavinde-slider-tab]');
      if (!tab || !this.contains(tab)) return;

      const tabs = this.tabs;
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      this.selectTab(nextIndex, true);
      this.tabs[nextIndex]?.focus({ preventScroll: true });
    }

    selectTab(index, trackEvent = false) {
      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          const track = panel.querySelector('[data-lavinde-slider-track]');
          if (track) track.scrollLeft = 0;
        }
      });

      this.attachTrackListeners();
      this.ensureMobileControls();
      this.renderDots();
      this.scrollTabIntoView(index, true);
      this.requestUpdate();

      if (trackEvent) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'lavinde_slider_tab_select',
          section_id: this.closest('[data-section-id]')?.dataset.sectionId || '',
          tab_index: index,
          tab_label: this.tabs[index]?.textContent?.trim() || ''
        });
      }
    }

    attachTrackListeners() {
      this.tracks.forEach((track) => {
        if (track.dataset.lavindeSliderScrollReady === 'true') return;
        track.dataset.lavindeSliderScrollReady = 'true';
        track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      });
    }

    ensureMobileControls() {
      this.panels.forEach((panel) => {
        const viewport = panel.querySelector('[data-lavinde-slider-viewport]');
        if (!viewport) return;
        if (panel.querySelector('[data-lavinde-slider-mobile-controls]')) return;

        const controls = document.createElement('div');
        controls.className = 'lavinde-slider-tabs-v2__mobile-controls';
        controls.setAttribute('data-lavinde-slider-mobile-controls', '');
        controls.hidden = true;

        const prev = this.createMobileArrow('prev');
        const dots = document.createElement('div');
        dots.className = 'lavinde-slider-tabs-v2__dots';
        dots.setAttribute('data-lavinde-slider-dots', '');
        dots.setAttribute('aria-label', 'Produktnavigation');

        const next = this.createMobileArrow('next');

        controls.appendChild(prev);
        controls.appendChild(dots);
        controls.appendChild(next);
        viewport.insertAdjacentElement('afterend', controls);
      });
    }

    createMobileArrow(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `lavinde-slider-tabs-v2__mobile-arrow lavinde-slider-tabs-v2__mobile-arrow--${direction}`;
      button.setAttribute(direction === 'prev' ? 'data-lavinde-slider-prev' : 'data-lavinde-slider-next', '');
      button.setAttribute('aria-label', direction === 'prev' ? 'Forrige produkt' : 'Næste produkt');

      const span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = direction === 'prev' ? '‹' : '›';
      button.appendChild(span);

      return button;
    }

    renderDots() {
      this.panels.forEach((panel) => {
        const dotsContainer = panel.querySelector('[data-lavinde-slider-dots]');
        const track = panel.querySelector('[data-lavinde-slider-track]');
        const pageTargets = !panel.hidden && track ? this.pageTargetsForTrack(track) : [];
        if (!dotsContainer) return;

        if (
          Number(dotsContainer.dataset.dotCount || 0) === pageTargets.length &&
          dotsContainer.children.length === pageTargets.length
        ) {
          return;
        }

        dotsContainer.dataset.dotCount = String(pageTargets.length);
        dotsContainer.textContent = '';

        pageTargets.forEach((target, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lavinde-slider-tabs-v2__dot';
          dot.setAttribute('data-lavinde-slider-dot', String(index));
          dot.setAttribute('aria-label', `Gå til produktvisning ${index + 1}`);
          dotsContainer.appendChild(dot);
        });
      });
    }

    scrollActiveTrack(direction, mobileControl) {
      const track = this.activeTrack;
      if (!track) return;

      if (mobileControl || window.matchMedia('(max-width: 749px)').matches) {
        const pageTargets = this.pageTargetsForTrack(track);
        const current = this.activePageIndex(track);
        const next = Math.max(0, Math.min(pageTargets.length - 1, current + direction));
        this.scrollToPageIndex(next);
        return;
      }

      const distance = Math.max(260, track.clientWidth * 0.86);
      track.scrollBy({ left: distance * direction, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
      window.setTimeout(() => this.requestUpdate(), this.prefersReducedMotion ? 0 : 240);
    }

    scrollToPageIndex(index) {
      const track = this.activeTrack;
      if (!track) return;

      const pageTargets = this.pageTargetsForTrack(track);
      const target = pageTargets[index];
      if (typeof target !== 'number') return;

      track.scrollTo({
        left: target,
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
      });
      this.requestUpdate();
    }

    scrollTabIntoView(index, animate = true) {
      const tab = this.tabs[index];
      const scroller = this.tabScroller;
      if (!tab || !scroller) return;
      if (scroller.scrollWidth <= scroller.clientWidth + 2) return;

      const targetLeft = Math.max(0, tab.offsetLeft - ((scroller.clientWidth - tab.offsetWidth) / 2));
      scroller.scrollTo({
        left: targetLeft,
        behavior: animate && !this.prefersReducedMotion ? 'smooth' : 'auto'
      });
    }

    requestUpdate() {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.updateArrows());
    }

    updateArrows() {
      const track = this.activeTrack;
      const activePanel = this.activePanel;
      const prevButtons = Array.from(this.querySelectorAll('[data-lavinde-slider-prev]'));
      const nextButtons = Array.from(this.querySelectorAll('[data-lavinde-slider-next]'));
      const mobileControls = Array.from(this.querySelectorAll('[data-lavinde-slider-mobile-controls]'));

      if (!track || !activePanel) {
        prevButtons.forEach((button) => {
          button.hidden = true;
          button.disabled = true;
        });
        nextButtons.forEach((button) => {
          button.hidden = true;
          button.disabled = true;
        });
        mobileControls.forEach((controls) => { controls.hidden = true; });
        return;
      }

      const maxScroll = this.maxScrollForTrack(track);
      const current = track.scrollLeft;
      const pageTargets = this.pageTargetsForTrack(track);
      const hasOverflow = maxScroll > 4 && pageTargets.length > 1;
      const activeIndex = this.activePageIndex(track);
      const atStart = activeIndex <= 0 || current <= 2;
      const atEnd = activeIndex >= pageTargets.length - 1 || current >= maxScroll - 2;

      prevButtons.forEach((button) => {
        const controls = button.closest('[data-lavinde-slider-mobile-controls]');
        const belongsToActivePanel = !controls || controls.closest('[data-lavinde-slider-panel]') === activePanel;
        button.hidden = !hasOverflow || !belongsToActivePanel;
        button.disabled = !hasOverflow || !belongsToActivePanel || atStart;
      });

      nextButtons.forEach((button) => {
        const controls = button.closest('[data-lavinde-slider-mobile-controls]');
        const belongsToActivePanel = !controls || controls.closest('[data-lavinde-slider-panel]') === activePanel;
        button.hidden = !hasOverflow || !belongsToActivePanel;
        button.disabled = !hasOverflow || !belongsToActivePanel || atEnd;
      });

      mobileControls.forEach((controls) => {
        const belongsToActivePanel = controls.closest('[data-lavinde-slider-panel]') === activePanel;
        controls.hidden = !hasOverflow || !belongsToActivePanel;
      });

      this.updateDots(activeIndex, hasOverflow, activePanel);
    }

    updateDots(activeIndex, hasOverflow, activePanel) {
      this.panels.forEach((panel) => {
        const isActivePanel = panel === activePanel;
        const dots = Array.from(panel.querySelectorAll('[data-lavinde-slider-dot]'));

        dots.forEach((dot, index) => {
          const selected = isActivePanel && index === activeIndex;
          dot.setAttribute('aria-current', selected ? 'true' : 'false');
          dot.disabled = !isActivePanel || !hasOverflow || selected;
        });
      });
    }

    activePageIndex(track = this.activeTrack) {
      const pageTargets = track ? this.pageTargetsForTrack(track) : [];
      if (!track || !pageTargets.length) return 0;

      const current = Math.min(track.scrollLeft, this.maxScrollForTrack(track));
      let active = 0;
      let distance = Infinity;

      pageTargets.forEach((target, index) => {
        const diff = Math.abs(target - current);
        if (diff < distance) {
          distance = diff;
          active = index;
        }
      });

      return active;
    }

    pageTargetsForTrack(track) {
      const items = track ? this.itemsForTrack(track) : [];
      if (!track || !items.length) return [];

      const maxScroll = this.maxScrollForTrack(track);
      const targets = [];
      const pushTarget = (value) => {
        const target = Math.max(0, Math.min(maxScroll, Math.round(value)));
        const isDuplicate = targets.some((existing) => Math.abs(existing - target) <= 2);
        if (!isDuplicate) targets.push(target);
      };

      pushTarget(0);

      items.forEach((item) => {
        if (item.offsetLeft <= maxScroll + 2) {
          pushTarget(item.offsetLeft);
        }
      });

      if (maxScroll > 4) {
        pushTarget(maxScroll);
      }

      return targets.sort((a, b) => a - b);
    }

    maxScrollForTrack(track) {
      return Math.max(0, Math.round((track?.scrollWidth || 0) - (track?.clientWidth || 0)));
    }

    itemsForPanel(panel) {
      return Array.from(panel.querySelectorAll('[data-lavinde-slider-item]'));
    }

    itemsForTrack(track) {
      return Array.from(track.querySelectorAll('[data-lavinde-slider-item]'));
    }

    get tabs() {
      return Array.from(this.querySelectorAll('[data-lavinde-slider-tab]'));
    }

    get panels() {
      return Array.from(this.querySelectorAll('[data-lavinde-slider-panel]'));
    }

    get tracks() {
      return Array.from(this.querySelectorAll('[data-lavinde-slider-track]'));
    }

    get activeTabIndex() {
      const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      return index >= 0 ? index : 0;
    }

    get tabScroller() {
      return this.querySelector('[role="tablist"]');
    }

    get activePanel() {
      return this.querySelector('[data-lavinde-slider-panel]:not([hidden])');
    }

    get activeTrack() {
      return this.activePanel?.querySelector('[data-lavinde-slider-track]');
    }

    get activeItems() {
      return this.activeTrack ? this.itemsForTrack(this.activeTrack) : [];
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define(ELEMENT_NAME, LavindeSliderTabsV2);
})();
